import { reloadAppAsync } from "expo";
import { reloadAsync } from "expo-updates";

import { ErrorBoundaryEvent } from "@/constants/analytics/Analytics";
import { TextTestID } from "@/constants/TestID";
import { useAnalytics } from "@/services/Analytics";
import FullScreenImageBackground from "@/ui/components/onboarding/FullScreenImageBackground";
// Direct imports avoid evaluating the @/ui/core barrel, which pulls in all IconDefinitions at startup
import Button from "@/ui/core/buttons/Button";
import EquipLogoInline from "@/ui/core/EquipLogoInline";
import EquipStatusBar from "@/ui/core/EquipStatusBar";
import { FAIconName } from "@/ui/core/Icon/IconDefinitions";
import Text from "@/ui/core/Text";
import { VStack } from "@/ui/layout";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

const ErrorBoundaryUI = () => {
  const baseTestID = "error-boundary";
  const { track } = useAnalytics();

  const handleRestart = async () => {
    try {
      track(ErrorBoundaryEvent.RestartAppPressed);
    } catch {
      // Could not log event to MixPanel
    } finally {
      // Wait 100 milliseconds before reloading the app to ensure that the analytics event
      // is tracked before the app is reloaded
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // On certain platform (Android), reloadAsync will not do anything on debug builds
    // reloadAppAsync emulates the behavior closely
    if (__DEV__) {
      await reloadAppAsync();
    } else {
      await reloadAsync();
    }
  };

  return (
    <FullScreenImageBackground testID={baseTestID}>
      <EquipStatusBar />
      <VStack
        flex={1}
        gap={24}
        justifyContent="center"
        padding={LayoutToken.OnboardingScreenHorizontalPadding}
      >
        <EquipLogoInline />
        <VStack gap={12}>
          <Text
            color={ColorToken.Primary90Variant}
            testID={joinTestIDs(baseTestID, TextTestID.Title)}
            variant="h2"
          >
            Hmmm... it looks like something went wrong
          </Text>
          <Text
            color={ColorToken.Primary90Variant}
            testID={joinTestIDs(baseTestID, TextTestID.Description)}
            variant="body"
          >
            Please restart the app and try again
          </Text>
        </VStack>
        <Button
          isFullWidth
          leftIcon={FAIconName.Refresh}
          onPress={handleRestart}
          testID="restart-app"
          title="Restart app"
          variant="primary.on_background"
        />
      </VStack>
    </FullScreenImageBackground>
  );
};

export default ErrorBoundaryUI;
